import { useState } from "react";

import { Input } from "../../../components/Input/Input";
import { FieldRow } from "../../projects/config/FieldRow";
import { SectionCard } from "../../projects/config/SectionCard";
import styles from "./BotsSettings.module.css";

interface Props {
  defaultLocale: string;
  localeByCountry: Record<string, string>;
  onDefaultLocale: (next: string) => void;
  onLocaleByCountry: (next: Record<string, string>) => void;
  dirty: boolean;
}

/**
 * `bots.default_locale` and `bots.locale_by_country` — which language a guild's
 * bots speak, and which clock their day follows.
 *
 * Mockup: bots-page-shell.html §4, "Locales". Moved verbatim from the Config
 * page's Bots section.
 */
export function LocalesSection({
  defaultLocale,
  localeByCountry,
  onDefaultLocale,
  onLocaleByCountry,
  dirty,
}: Props) {
  return (
    <SectionCard
      id="locales"
      title="Locales"
      apiName="bots.default_locale · bots.locale_by_country"
      dirty={dirty}
    >
      <FieldRow
        name="Default locale"
        apiName="bots.default_locale"
        hint="Used when a guild's country maps to a locale with no phrases."
      >
        <Input
          value={defaultLocale}
          onChange={(e) => onDefaultLocale(e.target.value.toLowerCase())}
          placeholder="en"
          aria-label="Default locale"
        />
      </FieldRow>

      <FieldRow
        name="Locale by country"
        apiName="bots.locale_by_country"
        hint={
          <>
            A guild's country (<code>guilds.country</code>, the dominant country
            of its living members) picks both the language bots speak and the
            timezone their daily rhythm follows.
          </>
        }
      >
        <LocaleMap value={localeByCountry} onChange={onLocaleByCountry} />
        <div className={styles.helper}>
          Countries not listed fall back to the default locale and to UTC.
        </div>
      </FieldRow>
    </SectionCard>
  );
}

function LocaleMap({
  value,
  onChange,
}: {
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const [country, setCountry] = useState("");
  const [locale, setLocale] = useState("");
  const entries = Object.entries(value).sort(([a], [b]) => a.localeCompare(b));

  const add = () => {
    const cc = country.trim().toUpperCase();
    const lc = locale.trim().toLowerCase();
    if (cc.length !== 2 || lc === "") return;
    onChange({ ...value, [cc]: lc });
    setCountry("");
    setLocale("");
  };

  return (
    <>
      <div className={styles.chips}>
        {entries.length === 0 && (
          <span className={styles.chipEmpty}>
            No countries mapped — every guild speaks the default locale.
          </span>
        )}
        {entries.map(([cc, lc]) => (
          <span key={cc} className={styles.chip}>
            {cc} → {lc}
            <button
              type="button"
              className={styles.chipRemove}
              aria-label={`Remove ${cc}`}
              onClick={() => {
                const next = { ...value };
                delete next[cc];
                onChange(next);
              }}
            >
              ✕
            </button>
          </span>
        ))}
      </div>
      <div className={styles.localeAdd}>
        <Input
          value={country}
          onChange={(e) => setCountry(e.target.value.toUpperCase())}
          placeholder="RU"
          aria-label="Country code"
          maxLength={2}
        />
        <span className={styles.dash}>→</span>
        <Input
          value={locale}
          onChange={(e) => setLocale(e.target.value.toLowerCase())}
          placeholder="ru"
          aria-label="Locale"
        />
        <button
          type="button"
          className={styles.rowBtn}
          onClick={add}
          aria-label="Add mapping"
        >
          +
        </button>
      </div>
    </>
  );
}
